<template>
    <div class="document-list-wrapper container-fluid p-t-25 p-r-0 p-l-0">
        <!-- ERROR MESSAGES -->
        <div class="raised low p-25 text-center" v-if="docTypes.length == 0" v-cloak>
            You do not have access to the documents on this page.
        </div>

        <div class="raised low p-25 text-center" v-else-if="errorMessage" v-cloak>
            {{ errorMessage }}
        </div>

        <div class="raised low p-25 text-center" v-else-if="accounts.length == 0 && user_is_sys_admin" v-cloak>
            System administrators must select an account to view documents
        </div>

        <div class="raised low p-25 text-center" v-else-if="docTypes.length > 1 && selectedDocTypeCodes.length == 0" v-cloak>
            Please select a document type to view documents
        </div>

        <div class="raised low p-25 text-center" v-else-if="is_retreiving_documents" style="font-size:1.5em" v-cloak>
            Loading...
        </div>

        <!-- DOCUMENT LIST -->
        <div class="document-list-container" v-else>
            <document-list v-show="!errorMessage && !is_retreiving_documents && documents.length > 0"
                           :documents="currentDocuments"
                           :all_expanded="allExpanded"
                           :document_type="document_type"
                           @toggle_selection="toggleSelection"
                           @toggle_details="toggleDetails"
                           @search="performSearch"
                           @show_partial_results="showPartialResults"
                           @change_view="goToGrid"
                           @reset_page="resetPage"
                           @go_to_old_portal="initiateOldPortalLink"
            ></document-list>

            <paginator class="p-t-10 p-r-25 p-b-10 p-r-25"
                       :available_page_sizes="[50, 100, 250, 500]"
                       :initial_page_size="documentsPerPage"
                       :current_page="page"
                       :current_page_size="documentsPerPage"
                       :number_of_items="totalDocuments"
                       :filtered_from="documents.length"
                       :disabled="is_retreiving_documents"
                       @change="changePagination"
            ></paginator>
        </div>

        <!-- PARTIAL RESULTS MODAL -->
        <div ref="partialResultsModal" class="modal fade" role="dialog" tabindex="-1">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <i class="fa fa-close close" data-dismiss="modal" aria-label="Close" aria-hidden="true"></i>
                        <h4 class="modal-title" id="exampleModalLabel">Partial Results</h4>
                    </div>
                    <div class="modal-body" style="max-height:calc(100vh - 250px);overflow-y:auto">
                        <div class="modal-subheader" v-if="partialReportResultsFormatted.length > 0">
                            <i class="fa fa-info-circle modal-subheader-icon"></i>
                            <span class="text-bold">
                                These analytical results are preliminary and are subject to change pending final approval
                            </span>
                        </div>
                        <div class="text-bold text-center p-25" v-if="loadingPartialResults">
                            Loading...
                        </div>
                        <transition-group name="fade" tag="div" v-else-if="partialReportResultsFormatted.length > 0">
                            <div v-for="(sample) in partialReportResultsFormatted" :key="sample.id">
                                <h3 class="m-l-25">Sample {{ sample.id }}</h3>
                                <table class="table table-condensed table-striped">
                                    <tbody>
                                    <tr v-for="item in sample.items">
                                        <td>{{ item.caption }}<span v-if="item.basis">&nbsp;({{ item.basis }})</span></td>
                                        <td>
                                            {{ item.value }}
                                            <span class="m-l-5" v-if="item.unitsMatch === undefined || item.unitsMatch">{{ item.units }}</span>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </transition-group>
                        <div class="p-25" v-else>
                            Unable to retrieve results
                        </div>

                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-lg btn-secondary" data-dismiss="modal"><i class="fa fa-times"></i> Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    @import 'resources/assets/sass/global_variables';
</style>

<script>
    import {mapState, mapGetters, mapActions} from 'vuex';

    module.exports = {
        props: {
            user_is_sys_admin: { default: null },
            accounts: { default: function(){ return [] } },
            report_finder: { default: function() { return { selected: false, items: [] }}},
            document_type : String,
            selectedPackages : Array,
            has_cc_hold: { type: Boolean, default: false },
        },

        data: function() {
            return {
                account_nbrs: [],
                initialReportCount: 500,
                maxRecords: 5000,
                page: 1,
                documentsPerPage: 100,
                is_retreiving_documents: false,
                errorMessage: null,
                partialReportResults: [],
                loadingPartialResults: false,
                loadingReportDocs: false,
                old_portal_url: null
            }
        },

        computed: {
            ...mapState('documents', {
                docTypes: 'docTypes',
                documents: 'rows',
                startDate: 'startDate',
                endDate: 'endDate',
                needToSearch: 'needToSearch',
                selectedSampleTypes: 'selectedSampleTypes',
                selectAllChecked: 'selectAllChecked'
            }),

            ...mapGetters('documents', {
                visibleDocuments: 'specificPageRows',
                filteredDocuments: 'visibleRows',
                selectedDocTypeCodes: 'selectedDocTypeCodes',
                selectedRows: 'selectedRows'
            }),

            totalDocuments: function() {
                return this.filteredDocuments.length;
            },

            currentDocuments: function() {
                return this.visibleDocuments({
                    page: this.page,
                    itemsPerPage: this.documentsPerPage
                })
            },

            partialReportResultsFormatted: function() {
                let samples = [];
                for (let key in this.partialReportResults) {
                    if (!this.partialReportResults.hasOwnProperty(key)) continue;
                    samples.push({ id: key, items: this.partialReportResults[key] });
                }
                return samples;
            },

            allExpanded () {
                return this.currentDocuments.filter(doc => doc.expandable && !doc.expanded).length == 0
            }
        },

        watch: {
            currentDocuments () {
                this.getReportDocuments();
            },

        },

        methods: {
            ...mapActions('documents', [
                'updateRows','searchUpdated','selectRow','deselectRow', 'checkSelectAllButton',
                'changeStartDate','changeEndDate','setItemValue'
            ]),

            resetPage () {
                this.page = 1;
            },

            initiateOldPortalLink: function(href) {
                if (href) {
                    this.old_portal_url = null;
                    const selected_accounts = this.accounts.map(account => { return account.account_nbr });
                    href = href.replace(/\/$/,'');
                    if (selected_accounts.length == 1) {
                        this.old_portal_url = href+'/'+selected_accounts[0];
                        this.goToOldPortal();
                    }
                    else {
                        this.old_portal_url = href;
                        this.$refs.oldPortalAccountPicker.showSelector();
                    }
                }
            },

            goToOldPortal (account) {
                if (this.old_portal_url) {
                    mwl.submitHiddenForm(this.old_portal_url, null, 'GET');
                }
            },

            goToGrid: function() {
                this.$emit('change_view','results');
            },

            queueSearch: function() {
                if (this.accounts.length > 0) {
                    this.performSearch();
                }
            },

            performSearch: function() {
                if (!this.needToSearch) {
                    return;
                }
                this.errorMessage = null;
                this.page = 1;
                this.getDocuments(function(response) {
                    if (this.documents.length == 0) this.errorMessage = 'No documents found.  Please refine your search and try again.';
                }, function(response) {
                    this.errorMessage = 'There was an error retrieving the documents.  You may need to narrow the date range on your search.';
                });
            },

            getDocuments: function(success,error) {
                let vm = this;
                this.is_retreiving_documents = true;
                this.documents.splice(0,this.documents.length);
                this.updateRows({ rows: [], filter_data: [] });
                let params = {
                    docTypes: this.selectedDocTypeCodes,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    maxRecords: this.maxRecords,
                    hasCcHold: this.has_cc_hold,
                    //resultsPerPage: this.documentsPerPage,
                    //pageNumber: this.page
                };
                if (this.report_finder.selected) {
                    params.reportFinderItems = this.report_finder.items;
                }
                if (this.document_type === 'report' && !this.startDate && !this.endDate) {
                    const momentFormat = 'YYYY-MM-DD';
                    const today = moment();
                    this.changeEndDate(today.format(momentFormat));
                    params.maxRecords = this.initialReportCount;
                }
                params.accountNbrs = [];
                for (let i=0;i<this.accounts.length;i++) {
                    params.accountNbrs.push(this.accounts[i].account_nbr);
                }
                if (this.selectedSampleTypes.length > 0) {
                    params.sampleTypes = this.selectedSampleTypes;
                }
                if (this.selectedPackages.length > 0) {
                    params.projectIDs = this.selectedPackages.map(pck => pck.id);
                }
                let request = this.$http.post('/documents/list', params);
                request.then(function(response) {
                    if (response.data) {
                        if (this.report_finder.selected && response.data.documents.length > 5) {
                            this.errorMessage = "This function was designed to help find a specific report.  Your search resulted in more than 5 matches.  Please refine your search and try again.";
                            return;
                        }
                        this.updateRows({
                            rows: response.data.documents,
                            filter_data: response.data.documents.map(function (document) {
                                return document.details
                            })
                        });
                        if (response.data.startDate && response.data.startDate != vm.startDate) {
                            const notify = (vm.startDate);
                            vm.changeStartDate(response.data.startDate);
                            if (notify) {
                                swal('The start date has been modified because your search resulted in more than ' + vm.maxRecords + ' records.');
                            }
                        }
                        else if (response.data.endDate && response.data.endDate != vm.endDate) {
                            const notify = (vm.endDate);
                            vm.changeEndDate(response.data.endDate);
                            if (notify) {
                                swal('The end date has been modified because your search resulted in more than ' + vm.maxRecords + ' records.');
                            }
                        }
                        this.searchUpdated(false);
                        if (typeof success === 'function') success.call(this,response);
                    }
                    this.is_retreiving_documents = false;
                }, function(response) {
                    this.documents.splice(0,this.documents.length);
                    this.is_retreiving_documents = false;
                    if (typeof error === 'function') error.call(this,response);
                });
            },

            getReportDocuments: function(success) {
                if (this.loadingReportDocs) return

                let reports = this.currentDocuments.filter(document =>
                    document.type.code == 'report' && !document.documentsRetrieved && (!document.versions || document.versions.length == 0)
                ).map(document => {
                    if (document.id.label) return document.id.label;
                    if (document.id.number) return document.id.number;
                    return false;
                });
                if (reports.length) {
                    this.loadingReportDocs = true;
                    let request = this.$http.get('/documents/reports/documents', { params: { reports: reports } });
                    request.then(function(response) {
                        if (response.data) {
                            response.data.documents.versions = response.data.documents.reports;
                            let itemTypes = ['versions','orderforms','billsummaries'];
                            let reportDocuments = {};
                            itemTypes.forEach(function(itemType) {
                                let items = (response.data.documents[itemType]) ? response.data.documents[itemType] : [];
                                for (let i = 0; i < items.length; i++) {
                                    if (!reportDocuments[items[i].reportNumber]) reportDocuments[items[i].reportNumber] = {};
                                    reportDocuments[items[i].reportNumber][itemType] = items[i].versions;
                                }
                            });
                            for (let i=0;i<this.currentDocuments.length;i++) {
                                let doc = this.currentDocuments[i];
                                let reportNumber = (doc.id.label) ? doc.id.label : doc.id.number;
                                if (reportDocuments[reportNumber]) {
                                    doc.versions = reportDocuments[reportNumber].versions ? reportDocuments[reportNumber].versions : [];
                                    doc.orderforms = reportDocuments[reportNumber].orderforms ? reportDocuments[reportNumber].orderforms : [];
                                    doc.billsummaries = reportDocuments[reportNumber].billsummaries ? reportDocuments[reportNumber].billsummaries : [];
                                    doc.data.report_btn = doc.versions;
                                    doc.data.order_form_btn = doc.orderforms;
                                    doc.data.billing_summary_btn = doc.billsummaries;
                                }
                                this.setItemValue({
                                    item: this.currentDocuments[i],
                                    key : 'documentsRetrieved',
                                    value: true
                                })
                            }
                        }
                        this.loadingReportDocs = false;
                        if (typeof success === 'function') success(response);
                    });
                }
            },

            showPartialResults: function(id) {
                this.partialReportResults = [];
                this.loadingPartialResults = true;
                this.$http.post('/results/reports', { report_ids: [id] })
                    .then((response) => {
                        if (response.data) {
                            this.partialReportResults = response.data;
                            this.loadingPartialResults = false;
                        }
                    });
                $(this.$refs.partialResultsModal).modal('show');
            },

            changePagination: function(pageNumber, itemsPerPage) {
                this.page = pageNumber;
                this.documentsPerPage = itemsPerPage;
            },

            toggleSelection: function(index) {
                if (index === undefined) {
                    if (this.selectAllChecked) {
                        this.deselectAll();
                    }
                    else {
                        this.selectAll();
                    }
                }
                else {
                    let item = _.has(this.currentDocuments,index) ? this.currentDocuments[index] : {};
                    if (item.selected) {
                        this.deselectItem(index);
                    }
                    else {
                        this.selectItem(index);
                    }
                }
            },

            toggleDetails (index) {
                if (index === undefined) {
                    let val = !this.allExpanded;
                    for(let i=0;i<this.currentDocuments.length;i++) {
                        if (this.currentDocuments[i].expandable) {
                            this.setItemValue({
                                item: this.currentDocuments[i],
                                key: 'expanded',
                                value: val
                            })
                        }
                    }
                }
                else {
                    let item = _.has(this.currentDocuments, index) ? this.currentDocuments[index] : {};
                    if (item.expandable) {
                        this.setItemValue({
                            item,
                            key: 'expanded',
                            value: !item.expanded
                        })
                    }
                }
            },

            selectItem: function(index) {
                let offset = this.documentsPerPage * (this.page - 1);
                let item = _.has(this.currentDocuments,index) ? this.currentDocuments[index] : {};

                if (!item.selectable) return;

                let document = this.currentDocuments[index];

                if (this.document_type === 'invoice') {
                    const invoice_type = _.has(document,['data','invoice_type']) ? document.data.invoice_type : null
                    let current_selection_invoice_type = this.selectedRows.map(row => {
                        return _.has(row,['data','invoice_type']) ? row.data.invoice_type : null
                    })
                    current_selection_invoice_type = current_selection_invoice_type[0] || null;

                    if (current_selection_invoice_type && current_selection_invoice_type != invoice_type) {
                        swal('','Monthly invoices and individual reports can not be paid together','info')
                        return
                    }
                }

                this.selectRow(offset + index);

                this.updateSelectAllCheckbox();
            },

            selectAll: function() {
                for(let i=0;i<this.filteredDocuments.length;i++) {
                    const document = this.filteredDocuments[i];
                    if (document.selectable) {
                        this.selectRow(i);
                    }
                }
                this.updateSelectAllCheckbox();
            },

            deselectItem: function(index) {
                let offset = this.documentsPerPage * (this.page - 1);
                this.deselectRow(offset + index);

                this.updateSelectAllCheckbox();
            },

            deselectAll: function() {
                for(let i=0;i<this.filteredDocuments.length;i++) {
                    const document = this.filteredDocuments[i];
                    if (document.selectable) {
                        this.deselectRow(i);
                    }
                }
                this.updateSelectAllCheckbox();
            },

            updateSelectAllCheckbox: function() {
                // Mark if every selectable document is selected
                const selectables = this.filteredDocuments.filter(doc => doc.selectable);
                // 'every' returns true if the array is empty
                this.checkSelectAllButton(selectables.length > 0 && selectables.every(doc => doc.selected));
            }
        }
    }
</script>



// WEBPACK FOOTER //
// document-list-standard-view.vue?6029b5b5